import { useState, useEffect, useId } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import LinearProgress from "@mui/material/LinearProgress";
import Snackbar from "@mui/material/Snackbar";
import TablePagination from "@mui/material/TablePagination";
import { useNavigate } from "react-router-dom";
import { useSiteList } from "./useSiteList";
import { buildSiteFilterObject } from "./BuildSiteFilterObject";
import { useSiteListActions } from "./useSiteListActions";
import type { CollectionSelection } from "./useSiteListActions";
import { useUpdateSiteParams } from "./useUpdateSiteParams";
import { SiteListFilters } from "./SiteListFilters";
import { emptyFiltersState, emptyPoolState, hasAppliedFilters } from "./EmptyFiltersExplanation";
import { SiteDataGrid } from "./SiteDataGrid";
import { SelectAllBanner } from "./SelectAllBanner";
import { VerifyAccessDialog } from "./VerifyAccessDialog";
import type { VerifyChoice } from "./VerifyAccessDialog";
import { DeactivateConfirmDialog } from "./DeactivateConfirmDialog";
import { VerificationProgress } from "./VerificationProgress";
import { CollectParamsDialog } from "./CollectParamsDialog";
import { CollectionProgress } from "./CollectionProgress";
import { ExportAccessStateDialog } from "./ExportAccessStateDialog";
import { UpdateParamsDialog } from "./UpdateParamsDialog";
import { EMPTY_FILTERS } from "./useSiteList";
import type { ExportSelection, SiteListFilters as SiteListFiltersModel } from "./useSiteList";
import { SiteSelection, EMPTY_SELECTION } from "./SiteSelection";
import type { SiteSelectionState, SelectableSite } from "./SiteSelection";
import { IfPermission } from "../../auth/IfPermission";
import type { JSX } from "react";

// Скелет таблицы состояния loading по скетчу SiteList.Loading.html: строка
// заголовка и семь строк, первая ячейка каждой строки шире остальных.
const SKELETON_ROWS = [1, 2, 3, 4, 5, 6, 7];
const SKELETON_CELLS = [1, 2, 3, 4, 5, 6];

export function SiteList() {
  let content: JSX.Element;
  const navigate = useNavigate();
  const { state, fetching, page, pageSize, filters, exportState, setFilters, setPage, setPageSize, refetch, exportAccessState } = useSiteList();
  const {
    verification,
    deactivation,
    collection,
    startVerification,
    cancelVerification,
    cancelCollection,
    deactivateSites,
    resetVerification,
    resetDeactivation,
    startCollection,
    resetCollection,
  } = useSiteListActions(refetch);
  const updateParameters = useUpdateSiteParams(refetch);
  const [selection, setSelection] = useState<SiteSelectionState>(EMPTY_SELECTION);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [collectDialogOpen, setCollectDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  // Ключ, размонтирующий и заново монтирующий панель фильтров при сбросе,
  // запрошенном пустым состоянием: панель стартует с EMPTY_FILTERS сама,
  // а экран отдельно применяет EMPTY_FILTERS к своему состоянию (см. requestFiltersReset).
  const [filtersResetKey, setFiltersResetKey] = useState(0);
  const emptyHeadingId = useId();
  useEffect(() => {
    if (deactivation.kind === "success") {
      setSelection(EMPTY_SELECTION);
    }
  }, [deactivation.kind]);
  const items = state.kind === "data" ? state.items : [];
  const totalCount = state.kind === "data" ? state.totalCount : 0;
  const pageSiteIds = items.map((item) => item.id);
  const bannerState = SiteSelection.computeSelectAllBannerState(selection, pageSiteIds, totalCount);
  const isAllFiltered = selection.mode === "allFiltered";
  const explicitSiteCount = SiteSelection.selectedSiteCount(selection);
  const selectedCredentials = SiteSelection.selectedCredentialIds(selection);
  const deactivateCount = isAllFiltered ? totalCount : explicitSiteCount;
  const deleteDisabled = !isAllFiltered && explicitSiteCount === 0;
  // Диалог обновления параметров — единственный модальный слой экрана: пока он
  // открыт, диалоги проверки, сбора, экспорта и удаления не показываются.
  const updateDialogShown = updateParameters.state.open;
  const verifyDialogShown = verifyDialogOpen && !updateDialogShown;
  const deactivateDialogShown = deactivateDialogOpen && !updateDialogShown;
  const collectDialogShown = collectDialogOpen && !updateDialogShown;
  const exportDialogShown = exportDialogOpen && !updateDialogShown;
  const goToSiteImport = () => {
    void navigate("/sites/create");
  };
  const handleFiltersChange = (newFilters: SiteListFiltersModel) => {
    setSelection(EMPTY_SELECTION);
    setFilters(newFilters);
  };
  const requestFiltersReset = () => {
    setFiltersResetKey((key) => key + 1);
    handleFiltersChange(EMPTY_FILTERS);
  };
  const handleToggleSite = (site: SelectableSite) => {
    setSelection((previous) => SiteSelection.toggleSite(previous, site));
  };
  const handleToggleCurrentPage = (pageSites: SelectableSite[]) => {
    setSelection((previous) => SiteSelection.toggleCurrentPage(previous, pageSites));
  };
  const handleSelectAllFiltered = () => {
    setSelection(SiteSelection.selectAllFiltered());
  };
  const handleClearSelection = () => {
    setSelection(EMPTY_SELECTION);
  };
  const handleVerifyConfirm = (choice: VerifyChoice) => {
    if (choice !== "selected") {
      startVerification({ selection: choice });
      return;
    }
    if (selection.mode === "allFiltered") {
      startVerification({ selection: "filtered", filters: buildSiteFilterObject(filters) });
    } else {
      startVerification({ selection: "selected", credentialIds: selectedCredentials });
    }
  };
  const handleDeactivateConfirm = () => {
    if (selection.mode === "allFiltered") {
      deactivateSites({ selection: "filtered", filters: buildSiteFilterObject(filters) });
    } else {
      deactivateSites({ selection: "selected", siteIds: SiteSelection.selectedSiteIds(selection) });
    }
    setDeactivateDialogOpen(false);
  };
  const handleCollectConfirm = (collectionSelection: CollectionSelection) => {
    const siteIds =
      collectionSelection === "selected" && selection.mode === "explicit"
        ? SiteSelection.selectedSiteIds(selection)
        : [];
    startCollection(collectionSelection, siteIds);
  };
  const handleExportConfirm = (exportSelection: ExportSelection) => {
    if (exportSelection === "selected" && selection.mode === "allFiltered") {
      exportAccessState("filtered", []);
      setExportDialogOpen(false);
      return;
    }
    const siteIds =
      exportSelection === "selected" && selection.mode === "explicit"
        ? SiteSelection.selectedSiteIds(selection)
        : [];
    exportAccessState(exportSelection, siteIds);
    setExportDialogOpen(false);
  };
  const showVerificationProgress = verification.kind !== "idle";
  const showCollectionProgress = collection.kind !== "idle";
  let collectionSummary: { totalSites: number; processedCount: number; successCount: number; failedCount: number } | null = null;
  if (collection.kind === "progress") {
    collectionSummary = {
      totalSites: collection.totalSites,
      processedCount: collection.processedCount,
      successCount: collection.successCount,
      failedCount: collection.failedCount,
    };
  }
  if (collection.kind === "completed") {
    collectionSummary = {
      totalSites: 0,
      processedCount: 0,
      successCount: collection.successCount,
      failedCount: collection.failedCount,
    };
  }

  // MUI считает страницы с нуля, экран — с единицы.
  const zeroBasedPage = page - 1;

  // Тулбар стоит во всех состояниях экрана в порядке и с акцентом скетча
  // SiteList.html; кнопка «Обновить параметры» — во всех состояниях, потому что
  // вставленный список не зависит от того, загрузился ли сам список сайтов.
  const toolbar = (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
      <Typography variant="h4">Сайты</Typography>
      <Box display="flex" gap={1}>
        <IfPermission permission="sites.verify">
          <Button
            variant="contained"
            onClick={() => setVerifyDialogOpen(true)}
            disabled={verification.kind === "starting" || verification.kind === "progress"}
          >
            Проверка доступов
          </Button>
        </IfPermission>
        <IfPermission permission="sites.params">
          <Button variant="outlined" onClick={() => setCollectDialogOpen(true)}>
            Собрать параметры
          </Button>
        </IfPermission>
        <IfPermission permission="sites.params">
          <Button variant="outlined" onClick={updateParameters.open}>
            Обновить параметры
          </Button>
        </IfPermission>
        <IfPermission permission="sites.view">
          <Button
            variant="outlined"
            onClick={() => setExportDialogOpen(true)}
            disabled={exportState.kind === "loading"}
          >
            Экспорт полного состояния
          </Button>
        </IfPermission>
        <IfPermission permission="sites.import">
          <Button variant="outlined" onClick={goToSiteImport}>
            Импорт сайтов
          </Button>
        </IfPermission>
        <IfPermission permission="sites.manage">
          <Button
            variant="outlined"
            color="error"
            disabled={deleteDisabled}
            onClick={() => setDeactivateDialogOpen(true)}
          >
            Удалить ({deactivateCount})
          </Button>
        </IfPermission>
      </Box>
    </Box>
  );

  if (state.kind === "loading") {
    content = (
      <Box role="progressbar" aria-label="Список сайтов загружается">
        <Box display="flex" gap={2} py={1}>
          {SKELETON_CELLS.map((cell) => (
            <Box key={cell} flex={1}>
              <Skeleton variant="text" />
            </Box>
          ))}
        </Box>
        {SKELETON_ROWS.map((row) => (
          <Box key={row} display="flex" gap={2} py={1} borderTop={1} borderColor="divider">
            {SKELETON_CELLS.map((cell) => (
              <Box key={cell} flex={cell === 1 ? 3 : 1}>
                <Skeleton variant="text" />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  } else if (state.kind === "error") {
    content = (
      <Box role="alert" textAlign="center" py={5}>
        <Typography variant="h6" gutterBottom>
          Список сайтов не загрузился
        </Typography>
        <Typography color="text.secondary" mb={2}>
          Сервер не ответил на запрос списка сайтов. Фильтры и поиск сохранены — повторите загрузку.
        </Typography>
        <Button variant="contained" onClick={refetch}>
          Повторить загрузку
        </Button>
      </Box>
    );
  } else if (state.kind === "empty") {
    const filtersApplied = hasAppliedFilters(filters) || filters.search.trim() !== "";
    const { heading, explanation } = filtersApplied
      ? emptyFiltersState(filters.search)
      : emptyPoolState();
    content = (
      <Box component="section" aria-labelledby={emptyHeadingId} textAlign="center" py={4}>
        <Typography id={emptyHeadingId} component="h2" variant="h6" gutterBottom>
          {heading}
        </Typography>
        <Typography color="text.secondary" mb={2}>
          {explanation}
        </Typography>
        <Box display="flex" gap={1} justifyContent="center">
          <IfPermission permission="sites.import">
            <Button variant="contained" onClick={goToSiteImport}>
              Импорт сайтов
            </Button>
          </IfPermission>
          {filtersApplied && (
            <Button variant="outlined" onClick={requestFiltersReset}>
              Сбросить фильтры
            </Button>
          )}
        </Box>
      </Box>
    );
  } else {
    content = (
      <>
        <SelectAllBanner
          state={bannerState}
          onSelectAllFiltered={handleSelectAllFiltered}
          onClearSelection={handleClearSelection}
        />
        <SiteDataGrid
          items={state.items}
          selection={selection}
          onToggleSite={handleToggleSite}
          onToggleCurrentPage={handleToggleCurrentPage}
        />
        <TablePagination
          component="div"
          count={state.totalCount}
          page={zeroBasedPage}
          onPageChange={(_event, newPage) => setPage(newPage + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => setPageSize(Number(event.target.value))}
          rowsPerPageOptions={[5, 10, 25, 50, 100, 250, 500, 1000]}
          labelRowsPerPage="На странице:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} из ${count}`
          }
        />
      </>
    );
  }

  return (
    <Box p={3}>
      {toolbar}

      <Box mb={2}>
        <SiteListFilters key={filtersResetKey} onChange={handleFiltersChange} />
      </Box>

      {fetching && <LinearProgress sx={{ mb: 1 }} />}

      {showCollectionProgress && (
        <CollectionProgress
          kind={collection.kind}
          summary={collectionSummary}
          errorMessage={collection.kind === "error" ? collection.message : null}
          onDismiss={resetCollection}
          onCancel={cancelCollection}
        />
      )}

      {showVerificationProgress && (
        <VerificationProgress
          kind={verification.kind}
          summary={
            verification.kind === "progress" || verification.kind === "completed"
              ? verification.summary
              : null
          }
          errorMessage={verification.kind === "error" ? verification.message : null}
          onDismiss={resetVerification}
          onCancel={cancelVerification}
        />
      )}

      {content}

      <UpdateParamsDialog
        open={updateDialogShown}
        request={updateParameters.state.request}
        selectedParameters={updateParameters.state.selectedParameters}
        rawData={updateParameters.state.rawData}
        lineFormat={updateParameters.lineFormat}
        nonEmptyLineCount={updateParameters.nonEmptyLineCount}
        maxLines={updateParameters.maxLines}
        summary={updateParameters.state.summary}
        errorMessage={updateParameters.state.errorMessage}
        copyNotFoundStatus={updateParameters.state.copyNotFoundStatus}
        copyInvalidStatus={updateParameters.state.copyInvalidStatus}
        onToggleParameter={updateParameters.toggleParameter}
        onRawDataChange={updateParameters.changeRawData}
        onSubmit={updateParameters.submit}
        onCancel={updateParameters.cancel}
        onCloseResult={updateParameters.closeResult}
        onReturnToInput={updateParameters.returnToInput}
        onDiscard={updateParameters.discard}
        onCopyNotFound={() => updateParameters.copy("notFound")}
        onCopyInvalidLines={() => updateParameters.copy("invalidLines")}
      />

      <CollectParamsDialog
        open={collectDialogShown}
        selectedCount={isAllFiltered ? 0 : explicitSiteCount}
        onClose={() => setCollectDialogOpen(false)}
        onConfirm={handleCollectConfirm}
      />

      <ExportAccessStateDialog
        open={exportDialogShown}
        selectedCount={isAllFiltered ? totalCount : explicitSiteCount}
        loading={exportState.kind === "loading"}
        onClose={() => setExportDialogOpen(false)}
        onConfirm={handleExportConfirm}
      />

      <VerifyAccessDialog
        open={verifyDialogShown}
        selectedCredentialCount={selectedCredentials.length}
        allFilteredSiteCount={isAllFiltered ? totalCount : null}
        onClose={() => setVerifyDialogOpen(false)}
        onConfirm={handleVerifyConfirm}
      />

      <DeactivateConfirmDialog
        open={deactivateDialogShown}
        count={deactivateCount}
        onClose={() => setDeactivateDialogOpen(false)}
        onConfirm={handleDeactivateConfirm}
      />

      <Snackbar
        open={deactivation.kind === "success"}
        autoHideDuration={4000}
        onClose={resetDeactivation}
        message={
          deactivation.kind === "success"
            ? `Удалено: ${deactivation.deactivatedCount}`
            : ""
        }
      />

      <Snackbar
        open={deactivation.kind === "error"}
        autoHideDuration={6000}
        onClose={resetDeactivation}
        message={deactivation.kind === "error" ? `Ошибка удаления: ${deactivation.message}` : ""}
      />

      <Snackbar
        open={exportState.kind === "error"}
        autoHideDuration={6000}
        message={exportState.kind === "error" ? `Ошибка экспорта: ${exportState.message}` : ""}
      />
    </Box>
  );
}
